import type { TFunction } from 'i18next';

// Stable machine-readable codes emitted by the auth service
// (dashboard-backend server/services/auth.mjs). These are the source of
// truth: text on the backend can change without breaking translation
// lookup here, as long as the code stays.
const CODE_TO_KEY: Record<string, string> = {
    INVALID_CREDENTIALS: 'authError.invalidCredentials',
    USER_NOT_VERIFIED: 'authError.userNotVerified',
    REQUIRED_FIELDS: 'authError.requiredFields',
    USER_EXISTS: 'authError.userExists',
    INVALID_TOKEN: 'authError.invalidLink',
    TOKEN_EXPIRED: 'authError.linkExpired',
    REGISTRATION_FAILED: 'authError.registrationFailed',
};

// Legacy fallback: the auth service used to emit only { error: '<string>' }
// without a code. Kept so this frontend still translates correctly if it
// happens to talk to a backend that predates the code-adding change (canary,
// rollback, cached edge response). Safe to delete once every environment has
// been on the code-emitting backend for long enough that no old responses
// are in flight.
const STRING_TO_KEY: Record<string, string> = {
    'Invalid email or password': 'authError.invalidCredentials',
    'User not verified': 'authError.userNotVerified',
    'Email and password are required': 'authError.requiredFields',
    'User already exists': 'authError.userExists',
    'User not found': 'authError.invalidLink',
    'Invalid token': 'authError.invalidLink',
    'Token expired': 'authError.linkExpired',
    'Registration failed': 'authError.registrationFailed',
};

// Pulls the error body out of whichever shape the caller has:
//   - 200-with-body response: source is `data`, so `data.error` / `data.code`
//   - axios rejection on 4xx: source is `error`, so `error.response.data.*`
// Returns an object exposing `{ code, error }` regardless of shape (either or
// both may be undefined), or undefined when nothing usable is present.
const extractErrorBody = (source: any): { code?: unknown; error?: unknown } | undefined => {
    if (source && (source.error !== undefined || source.code !== undefined)) {
        return { code: source.code, error: source.error };
    }
    const nested = source?.response?.data;
    if (nested && (nested.error !== undefined || nested.code !== undefined)) {
        return { code: nested.code, error: nested.error };
    }
    return undefined;
};

// Translates a backend auth failure into the user's current language.
//
// Preference order:
//   1. `code` → CODE_TO_KEY → t(key)          (preferred; stable contract)
//   2. `error` string → STRING_TO_KEY → t(key) (legacy backend without code)
//   3. raw `error` string                      (unmapped; honest but untranslated)
//   4. t(fallbackKey)                          (nothing usable in the response)
//
// Meant to be called from the alert() sites in the auth pages; consumers must
// be inside a component that has `t` from
// `useTranslation(undefined, { keyPrefix: 'Login' })`.
export const translateAuthError = (t: TFunction, source: any, fallbackKey: string): string => {
    const body = extractErrorBody(source);
    const code = typeof body?.code === 'string' ? body.code : undefined;
    if (code && CODE_TO_KEY[code]) return t(CODE_TO_KEY[code]);

    const message = typeof body?.error === 'string' ? body.error : undefined;
    if (message && STRING_TO_KEY[message]) return t(STRING_TO_KEY[message]);
    if (message) return message;

    return t(fallbackKey);
};
